import { PlazaService } from "../../../services/plaza.service.js";
import { PlazaCommentModal } from "./PlazaCommentModal.js";
import { ReportModal } from "../../modals/ReportModal.js";
import { PostComposerModal } from "./PostComposerModal.js";

/**
 * FeedCard Component
 * 
 * Renders a single post card for Ssulwha Club.
 * Handles Likes, Deletes, Lightbox clicks, and Comment modal trigger.
 * Uses PlazaService for data operations.
 */
export class FeedCard {
    /**
     * @param {string} id - Feed ID
     * @param {object} data - Feed Data (Firestore data)
     * @param {object} options - { isFlow } for specific visual modes
     */
    constructor(id, data, options = {}) {
        this.id = id;
        this.data = data;
        this.data = data;
        this.options = options; // e.g. { isFlow: true, activeCommentId: '...' }

        // [UX] Auto-Open Comment Modal (Target Lock)
        if (this.options.activeCommentId) {
            // Defer slightly to ensure DOM is ready
            setTimeout(() => {
                this.openCommentModal(this.element, this.id, this.data, this.options.activeCommentId);
            }, 800); // 800ms to allow card to slide in and settle
        }

        // Repost/Quote Logic
        this.type = data.type || 'normal'; // normal, repost, quote
        this.isRepost = this.type === 'repost';
        this.isQuote = this.type === 'quote';

        // The content to display as the "Core"
        this.originalFeed = this.isRepost ? data.repostData : (this.isQuote ? data.quoteData : null);

        // Sanity Check
        if ((this.isRepost || this.isQuote) && !this.originalFeed) {
            this.type = 'normal';
            this.isRepost = false;
            this.isQuote = false;
        }

        this.element = this.create();
    }

    render() {
        return this.element;
    }

    // Helper to format date
    formatDate(timestamp) {
        if (!timestamp) return '';
        const date = new Date(timestamp.seconds * 1000);
        const now = new Date();
        const diff = (now - date) / 1000;

        if (diff < 60) return '방금 전';
        if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`;
        return date.toLocaleDateString();
    }

    create() {
        const { id, data, options } = this;
        const card = document.createElement('div');

        // Base Style
        let containerClass = "relative bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl border border-white/20 dark:border-zinc-800 rounded-2xl p-6 shadow-sm transition-all group";

        if (options.isFlow) {
            // "Flow" mode adjustments (Horizontal scroll items)
            // [FIX] Added h-[420px] and justify-between for consistent height
            containerClass = "flex-shrink-0 w-[300px] md:w-80 h-[420px] p-5 bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer relative overflow-hidden group flex flex-col justify-between";

            // Flow mode click logic (Navigate to Plaza)
            card.onclick = () => window.router.navigate('/plaza');
        } else {
            // "Page" mode (Standard Feed)
            containerClass += " hover:shadow-md animate-slide-up";
        }

        // Admin Highlight
        if (data.isAdminContent) {
            if (options.isFlow) {
                card.classList.add('border-violet-200', 'dark:border-violet-800', 'border-2');
            } else {
                containerClass += ' ring-1 ring-violet-500/30 bg-violet-50/50 dark:bg-violet-900/10';
            }
        }

        card.className = containerClass;

        // --- Permissions ---
        const isOwner = window.user && window.user.uid === data.authorId;
        const isAdmin = window.user && (window.user.role === 'admin' || window.user.role === 'super_admin');
        const isLiked = data.likedBy && window.user && data.likedBy.includes(window.user.uid);

        // [REPOST LOGIC] Determine what to show
        let headerData = data;
        let contentData = data;
        let quoteBlock = '';
        let repostBanner = '';

        if (this.isRepost && this.originalFeed) {
            headerData = this.originalFeed;
            contentData = this.originalFeed;
            repostBanner = `
                <div class="flex items-center gap-2 text-xs text-gray-500 mb-3 font-bold select-none cursor-pointer hover:text-green-600 transition-colors" onclick="window.router.navigate('/user/${data.authorId}')">
                    <span class="material-symbols-outlined text-sm font-bold">repeat</span>
                    <span>${this.escapeHtml(data.authorName)}님이 공유함</span>
                </div>
            `;
        }

        if (this.isQuote && this.originalFeed) {
            const q = this.originalFeed;
            const qImg = (q.imageUrls && q.imageUrls[0]) || q.imageUrl || null;
            const mainHasImg = (contentData.imageUrls && contentData.imageUrls.length > 0) || contentData.imageUrl;

            // [UX] Compact Quote Mode: If Main Post has image, show Quote Image small on right
            const isCompact = mainHasImg && qImg;

            if (isCompact) {
                // Compact Layout (Text Left, Image Right)
                quoteBlock = `
                    <div class="mt-3 mb-3 border border-gray-200 dark:border-zinc-700 rounded-xl overflow-hidden cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors quote-container relative group/quote" data-id="${q.id}">
                        <div class="flex p-3 gap-3 h-full">
                            <div class="flex-grow flex flex-col justify-center min-w-0">
                                <div class="flex items-center gap-1.5 mb-1">
                                    <div class="quote-author-area flex items-center gap-1.5 cursor-pointer hover:underline decoration-gray-400" data-author-id="${q.authorId}">
                                        ${q.authorPhoto ?
                        `<img src="${q.authorPhoto}" class="w-4 h-4 rounded-full object-cover" alt="">` :
                        `<div class="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-[8px] font-bold text-gray-500">${this.escapeHtml(q.authorName)?.[0]}</div>`
                    }
                                        <span class="font-bold text-xs text-gray-900 dark:text-gray-100 truncate max-w-[100px]">${this.escapeHtml(q.authorName)}</span>
                                    </div>
                                    <span class="text-[10px] text-gray-500">· ${this.formatDate(q.createdAt)}</span>
                                </div>
                                <p class="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 text-content leading-relaxed">${this.escapeHtml(q.content) || '사진을 공유했습니다.'}</p>
                            </div>
                            <div class="w-16 h-16 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden border border-gray-100 dark:border-zinc-700">
                                <img src="${qImg}" class="w-full h-full object-cover" loading="lazy" alt="">
                            </div>
                        </div>
                    </div>
                `;
            } else {
                // Standard Layout (Standard Header + Content)
                quoteBlock = `
                    <div class="mt-3 mb-3 border border-gray-200 dark:border-zinc-700 rounded-xl overflow-hidden cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors quote-container" data-id="${q.id}">
                        <div class="p-3">
                            <div class="flex items-center gap-1.5 mb-2">
                                <div class="quote-author-area flex items-center gap-1.5 cursor-pointer hover:opacity-75 transition-opacity" data-author-id="${q.authorId}">
                                     ${q.authorPhoto ?
                        `<img src="${q.authorPhoto}" class="w-5 h-5 rounded-full object-cover shadow-sm" alt="">` :
                        `<div class="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-[9px] font-bold text-gray-500">${this.escapeHtml(q.authorName)?.[0]}</div>`
                    }
                                    <span class="font-bold text-sm text-gray-900 dark:text-gray-100">${this.escapeHtml(q.authorName)}</span>
                                </div>
                                <span class="text-[10px] text-gray-400">· ${this.formatDate(q.createdAt)}</span>
                            </div>
                            <!-- Quote Content -->
                            <div class="flex gap-3">
                                <div class="flex-grow min-w-0">
                                     <p class="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 leading-relaxed">${this.escapeHtml(q.content)}</p>
                                </div>
                                ${qImg ? `<div class="w-20 h-20 rounded-lg bg-gray-100 shrink-0 overflow-hidden"><img src="${qImg}" class="w-full h-full object-cover" loading="lazy" alt=""></div>` : ''}
                            </div>
                        </div>
                    </div>
                `;
            }
        }

        // --- Render Parts ---
        const imageGridHTML = this.renderImageGrid(contentData);
        const headerHTML = this.renderHeader(headerData);
        const contentHTML = this.renderContent(contentData);

        // [REFACTORED] Use internal helpers for modularity
        // (Moved footer/events to helpers, others kept inline for safety in Step 1)

        // --- Template ---
        card.innerHTML = `
            ${repostBanner}
            ${headerHTML}
            ${contentHTML}
            ${quoteBlock}
            ${imageGridHTML}
            ${this._renderFooter(data, isLiked, options)}
        `;

        // --- Event Binding ---
        if (options.isFlow) return card;

        this._bindEvents(card, headerData, id, data);

        return card;
    }

    // --- Internal Modular Helpers (v0.749 Refactor) ---

    _renderFooter(data, isLiked, options) {
        // [UX] Standard Thread vs Comment Distinction (Confirmed by User)
        // Comment: Bubble Icon (Always, unless it's a Thread)
        // Thread: Tree Icon + "스레드 이어서 보기" Text (Only if created via Thread Button)
        const isThread = data.hasThread || (data.commentCount > 0 && data.threadCount > 0);
        const commentIcon = isThread ? 'account_tree' : 'chat_bubble';
        // Indigo for Thread, Gray/Default for Comment
        const commentColorClass = isThread ? 'text-indigo-500' : 'text-gray-400 hover:text-blue-500';

        return `
            <div class="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-zinc-800 ${options.isFlow ? 'mt-auto pointer-events-none' : ''}">
                <div class="flex items-center gap-4 text-xs font-bold text-gray-500">
                     <!-- Like Button -->
                    <button class="btn-like flex items-center gap-1.5 transition-colors group/like ${isLiked ? 'text-yellow-400' : 'text-gray-400 hover:text-yellow-400'}">
                        <span class="material-symbols-outlined text-[18px] group-hover/like:animate-bounce" style="${isLiked ? "font-variation-settings: 'FILL' 1;" : ''}">star</span>
                        <span class="text-sm font-medium like-count">${data.likes || 0}</span>
                    </button>
                    
                    <!-- Comment Button -->
                    <button class="btn-comment flex items-center gap-1.5 transition-colors ${commentColorClass}">
                        <span class="material-symbols-outlined text-[18px]">${commentIcon}</span>
                        <span class="text-sm font-medium comment-count">${data.commentCount || 0}</span>
                        <span class="thread-text text-[11px] font-bold ${isThread ? '' : 'hidden'}">스레드 이어서 보기</span>
                    </button>

                    <!-- Repost Button -->
                    <button class="btn-repost flex items-center gap-1.5 text-gray-400 hover:text-green-500 transition-colors group/repost">
                        <span class="material-symbols-outlined text-[20px] group-hover/repost:rotate-180 transition-transform duration-500">repeat</span>
                        <span class="text-xs font-medium count-repost">${data.repostCount || 0}</span>
                    </button>
                    
                    <!-- View Count -->
                    <div class="flex items-center gap-1.5 text-gray-500" title="조회수">
                        <span class="material-symbols-outlined text-[18px]">visibility</span>
                        <span class="text-sm font-medium view-count">${Math.max(data.views || 0, data.viewCount || 0, data.stats?.viewCount || 0)}</span>
                    </div>

                    <!-- ENOM Button -->
                    <button class="btn-enom flex items-center gap-1.5 text-gray-300 hover:text-red-500 transition-colors ml-2" title="이모지">
                        <span class="material-symbols-outlined text-[18px]">gavel</span>
                    </button>
                </div>
                
                ${options.isFlow ? `` : `<div class="admin-actions"></div>`}
            </div>
        `;
    }

    _bindEvents(card, headerData, id, data) {
        // 1. Author Link
        const authorLink = card.querySelector('.author-link');
        const authorAvatar = card.querySelector('.author-avatar');

        // [FIX] Disable Profile Link for System/Bot Posts
        const isSystem = headerData.authorName === 'SsulwhaWorld' || headerData.authorName === '썰화 운영진' || headerData.authorId === 'system';

        const navProfile = (e) => {
            if (isSystem) return; // Guard
            e.stopPropagation();
            window.router.navigate(`/user/${headerData.authorId || '#'}`);
        };

        if (authorLink && !isSystem) authorLink.onclick = navProfile;
        if (authorAvatar && !isSystem) authorAvatar.onclick = navProfile;

        // 2. Hashtags
        card.querySelectorAll('.hashtag-link').forEach(tag => {
            tag.onclick = (e) => {
                e.stopPropagation();
                window.router.navigate(`/plaza?tag=${tag.innerText.replace('#', '')}`);
            };
        });

        // 3. Quote Navigation
        const quoteContainer = card.querySelector('.quote-container');
        if (quoteContainer) {
            quoteContainer.onclick = (e) => {
                e.stopPropagation();
                window.router.navigate(`/plaza?id=${quoteContainer.dataset.id}`);
            };
            const quoteAuthor = quoteContainer.querySelector('.quote-author-area');
            if (quoteAuthor) {
                quoteAuthor.onclick = (e) => {
                    e.stopPropagation();
                    window.router.navigate(`/user/${quoteAuthor.dataset.authorId}`);
                };
            }
        }

        // 4. Interaction Buttons
        const btnLike = card.querySelector('.btn-like');
        if (btnLike) btnLike.onclick = (e) => { e.stopPropagation(); this.handleLike(btnLike); };

        // [FIX] Bind to ALL comment buttons (Footer + Thread Indicator) and Sync Updates
        // Uses helper to allow re-binding
        this._rebindThreadButton(card, id, data);

        const btnRepost = card.querySelector('.btn-repost');
        if (btnRepost) btnRepost.onclick = (e) => { e.stopPropagation(); this.handleRepostClick(); };

        const btnEnom = card.querySelector('.btn-enom');
        if (btnEnom) {
            btnEnom.onclick = (e) => {
                e.stopPropagation();
                if (!window.user) return this._requireLogin();
                ReportModal.show(data.authorId, id, 'feed', 'enom');
            };
        }

        // 5. Menu Actions (Share, Delete, Admin)
        this._bindMenuActions(card, id, data);
    }

    _requireLogin() {
        if (typeof Swal !== 'undefined') {
            Swal.fire({
                title: '로그인이 필요합니다',
                text: "이 기능을 이용하시려면 로그인이 필요합니다.",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4f46e5',
                cancelButtonColor: '#d33',
                confirmButtonText: '로그인',
                cancelButtonText: '취소'
            }).then((result) => {
                if (result.isConfirmed) window.router.navigate('/login');
            });
        } else {
            if (confirm("로그인이 필요합니다.")) window.router.navigate('/login');
        }
    }

    _bindMenuActions(card, id, data) {
        const menuContainer = card.querySelector('.action-menu');
        if (!menuContainer) return;

        const isAdmin = window.user && (window.user.role === 'admin' || window.user.role === 'super_admin');
        const isOwner = window.user && window.user.uid === data.authorId;

        // Share
        const btnShare = document.createElement('button');
        btnShare.className = "btn-share p-2 text-gray-400 hover:text-indigo-500 rounded-full transition-colors";
        btnShare.innerHTML = `<span class="material-symbols-outlined text-sm">link</span>`;
        btnShare.onclick = (e) => { e.stopPropagation(); this._handleShare(id); };
        menuContainer.appendChild(btnShare);

        // Delete (Owner OR Admin)
        // [FIX] Admin can delete ANY post
        if (isOwner || isAdmin) {
            const btnDelete = document.createElement('button');
            btnDelete.className = "btn-delete p-2 text-gray-400 hover:text-red-500 rounded-full transition-colors";
            btnDelete.innerHTML = `<span class="material-symbols-outlined text-sm">delete</span>`;
            // If Admin destroying someone else's post, maybe warn? _handleDelete handles confirmation.
            // But _handleDelete uses simple Swal. Standard flow is fine.
            btnDelete.onclick = (e) => { e.stopPropagation(); this._handleDelete(id, card); };
            menuContainer.appendChild(btnDelete);
        }

        // Admin (ENOM)
        if (isAdmin) {
            const btnEnom = document.createElement('button');
            btnEnom.className = "btn-enom-delete p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors";
            btnEnom.innerHTML = `<span class="material-symbols-outlined">gavel</span>`;
            btnEnom.onclick = (e) => { e.stopPropagation(); this._handleAdminEnom(id, data, card); };
            menuContainer.appendChild(btnEnom);
        }
    }

    // ... (Keep renderHeader, renderContent, etc.)

    // Stub for previous long methods being moved eventually
    // For now, I am only replacing the TAIL of create() and appending new methods.
    // The previous create() logic for repost/quote banner calculation stays for now to minimize diff risk,
    // but the Footer and Events are now modular.


    // --- Helper handlers (extracted from previous large blocks) ---
    _handleShare(id) {
        const url = window.location.origin + '/#/plaza?id=' + id;
        if (typeof Swal !== 'undefined') {
            Swal.fire({
                title: '<span class="text-xl font-bold text-gray-800 dark:text-gray-100">🔗 링크 공유</span>',
                html: `
                    <div class="flex flex-col gap-4 mt-2">
                        <div class="relative group">
                            <input type="text" value="${url}" readonly 
                                class="relative w-full p-3 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg text-sm text-gray-600 dark:text-gray-300 font-mono focus:outline-none focus:ring-2 focus:ring-violet-500 select-all text-center" 
                                id="share-link-input">
                        </div>
                        <button id="btn-manual-copy" class="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2">
                            <span class="material-symbols-outlined">content_copy</span> 링크 복사하기
                        </button>
                    </div>
                `,
                showConfirmButton: false,
                showCloseButton: true,
                didOpen: () => {
                    const input = document.getElementById('share-link-input');
                    const btn = document.getElementById('btn-manual-copy');
                    setTimeout(() => input.select(), 100);
                    btn.onclick = () => {
                        input.select();
                        navigator.clipboard.writeText(url).then(() => {
                            btn.innerHTML = `<span class="material-symbols-outlined">check_circle</span> 복사되었습니다!`;
                            btn.classList.replace('bg-violet-600', 'bg-green-600');
                        });
                    };
                }
            });
        }
    }

    _handleDelete(id, card) {
        Swal.fire({
            title: '정말 삭제하시겠습니까?',
            text: "삭제된 이야기는 복구할 수 없습니다.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: '삭제',
            cancelButtonText: '취소'
        }).then((result) => {
            if (result.isConfirmed) {
                PlazaService.deletePost(id).then(() => {
                    card.remove();
                    Swal.fire('삭제 완료', '삭제되었습니다.', 'success');
                }).catch(e => {
                    console.error(e);
                    const msg = e.code === 'permission-denied' ? '권한이 없습니다.' : '삭제 중 오류가 발생했습니다.';
                    Swal.fire('삭제 실패', msg, 'error');
                });
            }
        });
    }

    _handleAdminEnom(id, data, card) {
        Swal.fire({
            title: '관리자 심판 (ENOM)',
            html: `
                <div class="flex flex-col gap-2">
                    <button id="btn-delete-post" class="w-full bg-gray-100 hover:bg-gray-200 py-2 rounded font-bold text-gray-700">1. 게시글 삭제</button>
                    <button id="btn-ban-user" class="w-full bg-red-100 hover:bg-red-200 py-2 rounded font-bold text-red-700">2. 작성자 영구 제명</button>
                </div>
            `,
            showConfirmButton: false,
            didOpen: (popup) => {
                popup.querySelector('#btn-delete-post').onclick = () => {
                    PlazaService.deletePost(id).then(() => {
                        card.remove();
                        Swal.close();
                        Swal.fire('삭제 완료', '삭제되었습니다.', 'success');
                    });
                };
                popup.querySelector('#btn-ban-user').onclick = () => {
                    Swal.fire({
                        title: '제명 확인',
                        text: `사용자 '${data.authorName}'을 제명하시겠습니까?`,
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#d33'
                    }).then((r) => {
                        if (r.isConfirmed) {
                            PlazaService.banUserAndClearContent(data.authorId).then(() => {
                                card.remove();
                                Swal.close();
                                Swal.fire('제명 완료', '처리되었습니다.', 'success');
                            });
                        }
                    });
                };
            }
        });
    }

    renderHeader(data) {
        const isOwner = window.user && window.user.uid === data.authorId;
        const isAdmin = data.isAdminContent;

        // [FIX] Disable Profile Link for System/Bot Posts
        // SsulwhaWorld, 썰화 운영진, etc. are system accounts with no profile.
        const isSystem = data.authorName === 'SsulwhaWorld' || data.authorName === '썰화 운영진' || data.authorId === 'system';
        const profileCursor = isSystem ? 'cursor-default' : 'cursor-pointer';
        const profileHover = isSystem ? '' : 'hover:opacity-80';
        const linkHover = isSystem ? '' : 'hover:underline decoration-violet-500 underline-offset-2';
        const clickAction = isSystem ? '' : `onclick="window.router.navigate('/user/${data.authorId}')"`;

        return `
            <div class="flex items-start justify-between mb-3 relative group">
                <!-- Avatar Area -->
                <div class="flex items-center gap-3 relative">
                    <div class="relative ${profileCursor}" ${clickAction}>
                        <div class="author-avatar w-10 h-10 rounded-full overflow-hidden border border-gray-100 dark:border-zinc-700 ${profileCursor} ${profileHover} transition-opacity bg-white dark:bg-zinc-800 shrink-0 relative z-10">
                             ${data.authorPhoto
                ? `<img src="${data.authorPhoto}" class="w-full h-full object-cover" alt="">`
                : `<div class="w-full h-full flex items-center justify-center bg-violet-100 text-violet-600 font-bold text-sm select-none">${this.escapeHtml(data.authorName)?.[0] || 'U'}</div>`
            }
                        </div>
                    </div>
                    <div class="flex flex-col">
                        <div class="flex items-center gap-1.5">
                            <span class="author-link font-bold text-sm text-gray-900 dark:text-gray-100 ${profileCursor} ${linkHover}" ${clickAction}>
                                ${this.escapeHtml(data.authorName)}
                            </span>
                            ${isAdmin ? `<span class="material-symbols-outlined text-[14px] text-violet-500" title="관리자">verified</span>` : ''}
                        </div>
                        <span class="text-xs text-gray-500 font-medium tracking-wide">${this.formatDate(data.createdAt)}</span>
                    </div>
                </div>

                <!-- Action Menu Placeholder (populated in create) -->
                <div class="action-menu flex items-center gap-1"></div>
            </div>
        `;
    }

    escapeHtml(text) {
        if (!text) return '';
        return text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
    }

    renderContent(data) {
        if (!data.content && !data.text) return '';
        // [SECURITY] Escape HTML first to prevent XSS and broken layout
        const rawText = data.content || data.text || '';
        const escapedText = this.escapeHtml(rawText);

        // [UX] Truncate text only in Flow mode (Home screen)
        const lineClampClass = this.options.isFlow ? 'line-clamp-6' : '';

        // [INDENT FIX] Removed template literal whitespace
        return `
            <div class="mb-3 w-full clear-both" style="width: 100% !important; clear: both !important;">
                <div class="${lineClampClass} text-[15px] leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words text-left indent-0 tracking-normal text-content" style="text-align: left !important; text-indent: 0 !important; padding-left: 0 !important; margin-left: 0 !important; width: 100% !important;">${this.formatContent(escapedText)}</div>
            </div>
        `;
    }

    renderImageGrid(data) {
        const images = data.imageUrls || (data.imageUrl ? [data.imageUrl] : []);
        if (images.length === 0) return '';

        const count = images.length;
        let gridHTML = '';

        if (count === 1) {
            gridHTML = `
                <div class="rounded-xl overflow-hidden border border-gray-100 dark:border-zinc-800 bg-gray-50 dark:bg-black/50 flex justify-center h-[300px] md:h-auto">
                    <img src="${images[0]}" class="w-full h-full object-contain transition-transform duration-300 hover:scale-[1.02]" 
                         data-index="0" alt="">
                </div>
            `;
        } else if (count === 2) {
            gridHTML = `
                <div class="grid grid-cols-2 gap-2 rounded-xl overflow-hidden h-[200px] md:h-auto">
                    ${images.map((url, i) => `
                        <div class="aspect-square bg-gray-100 dark:bg-zinc-800">
                            <img src="${url}" class="w-full h-full object-cover hover:opacity-95" data-index="${i}" alt="">
                        </div>
                    `).join('')}
                </div>
            `;
        } else {
            // 3+ images logic
            gridHTML = `<div class="grid grid-cols-2 gap-1 rounded-xl overflow-hidden h-[250px] md:h-auto">`;
            images.forEach((url, i) => {
                // Determine span and aspect ratio based on count
                let containerClass = "bg-gray-100 dark:bg-zinc-800 overflow-hidden relative cursor-pointer";

                // If 3 images, first one is big (2 cols)
                if (count === 3 && i === 0) {
                    containerClass += " col-span-2 aspect-[2/1]";
                } else {
                    containerClass += " aspect-square";
                }

                // If 4 images, all square (handled by default grid)

                gridHTML += `
                    <div class="${containerClass}">
                        <img src="${url}" class="w-full h-full object-cover hover:opacity-95 transition-opacity" data-index="${i}" alt="">
                    </div>
                `;
            });
            gridHTML += `</div>`;
        }

        // [THREAD INDICATOR]
        // Explicit Thread (hasThread flag) OR High Probability Thread (High comment count with no explicit flag yet - backward compat)
        // For strictness, let's prioritize hasThread.
        const isThread = data.hasThread || (data.commentCount > 0 && data.threadCount > 0);

        if (isThread || data.commentCount > 0) {
            // [UX] Standardize "More in Thread" visual for ALL continuations (Text or Thread)
            // User requested that even text comments look like the "Thread" UI (Blue/Tree).
            const label = '스레드 이어서 보기'; // Unified Label
            const icon = 'account_tree';       // Unified Icon
            const colorClass = 'text-indigo-500'; // Unified Color
            const bgClass = 'bg-indigo-50 dark:bg-indigo-900/30'; // Unified Background

            gridHTML += `
                <div class="flex items-center gap-2 mb-2 px-1 group cursor-pointer btn-comment transition-all ${bgClass} p-1.5 rounded-lg w-fit">
                    <div class="w-0.5 h-3 bg-indigo-300 dark:bg-indigo-600 rounded-full"></div>
                    <span class="material-symbols-outlined text-[14px] ${colorClass}">${icon}</span>
                    <span class="text-xs font-bold ${colorClass} group-hover:underline thread-label">
                        ${label}
                    </span>
                </div>
             `;
        }

        return `<div class="mb-4 image-grid-container">${gridHTML}</div>`;
    }

    _rebindThreadButton(card, id, data) {
        const btnComments = card.querySelectorAll('.btn-comment');
        btnComments.forEach(btn => {
            // Avoid duplicate binding if possible, or simple replace
            btn.onclick = (e) => {
                e.stopPropagation();
                this.openCommentModal(card, id, data);
            };
        });
    }

    openCommentModal(card, id, data, targetCommentId = null) {
        // [UX] Optimistic View Count Update
        // Immediately increment view count in UI and Data to reflect the "View" action (opening modal)
        if (data) {
            // 1. Update Data
            data.views = (data.views || 0) + 1;
            // Also update legacy fields just in case
            if (data.viewCount !== undefined) data.viewCount = (data.viewCount || 0) + 1;
            if (data.stats && data.stats.viewCount !== undefined) data.stats.viewCount = (data.stats.viewCount || 0) + 1;

            // 2. Update Element (Find all instances in this card, usually one)
            const viewCountEls = card.querySelectorAll('.view-count');
            viewCountEls.forEach(el => {
                // Parse current, increment, or just use data.views
                el.innerText = Math.max(data.views, data.viewCount || 0);
            });
        }

        const onUpdate = (newCount) => {
            // Update Data with new count
            if (data) data.commentCount = newCount;

            // [FIX] Dynamic Footer Update (Count Only)
            // Icon/Text style depends on `hasThread` (Post Type), NOT just comment count.
            // Adding a comment (via Modal) should NOT change visual type to Thread.
            // So we ONLY update the number.
            const footerBtns = card.querySelectorAll('.btn-comment');
            footerBtns.forEach(btn => {
                const countSpan = btn.querySelector('.comment-count');
                if (countSpan) countSpan.innerText = newCount;
            });

            // [Refactor] Unified Update Logic: Always re-render the grid content
            // This guarantees that the dynamic state (comments added) EXACTLY matches the initial render state (Blue/Tree style).
            // It handles 0->1, 1->2, and visual updates simultaneously without fragile manual class manipulation.

            const container = card.querySelector('.image-grid-container');
            if (container) {
                // [FIX] For Reposts, we must merge Original Feed (Images) with Repost Data (Stats)
                // Otherwise renderImageGrid returns empty (no images in repost wrapper) causing crash
                const renderData = (this.isRepost && this.originalFeed)
                    ? { ...this.originalFeed, ...data } // Merge: Original images, Repost stats
                    : data;

                // Re-render grid HTML with updated data
                const newHTMLWrapper = this.renderImageGrid(renderData);

                // Extract inner content to preserve the container wrapper
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = newHTMLWrapper;

                const newContainer = tempDiv.querySelector('.image-grid-container');
                if (newContainer) {
                    container.innerHTML = newContainer.innerHTML;
                    // [CRITICAL] Re-bind events for the newly rendered button(s)
                    this._rebindThreadButton(card, id, data);
                }
            }
        };
        const modal = new PlazaCommentModal(id, data.authorId, onUpdate);

        // [UX] Anchoring: Set callback to highlight THIS card on close if user commented
        modal.onCloseCallback = () => {
            this.highlightSelf();
        };

        modal.render();

        // [UX] Target Lock Activation
        if (targetCommentId) {
            modal.scrollToComment(targetCommentId);
        }
    }

    // [UX] Anchoring Effect (Self Highlight)
    highlightSelf() {
        if (!this.element) return;

        // 1. Scroll to Center
        this.element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // 2. Glow Effect (Reuse Target Lock style but slightly bigger)
        this.element.classList.add('ring-4', 'ring-violet-500/50', 'shadow-[0_0_30px_rgba(139,92,246,0.3)]', 'transition-all', 'duration-1000');

        // 3. Remove after 2s
        setTimeout(() => {
            this.element.classList.remove('ring-4', 'ring-violet-500/50', 'shadow-[0_0_30px_rgba(139,92,246,0.3)]');
        }, 2000);
    }

    formatContent(content) {
        if (!content) return '';
        // Content is already escaped by renderContent, so it's safe to inject spans here
        let formatted = content.replace(/(#[\w가-힣\[\]\(\)\-\.]+)/g, '<span class="hashtag-link text-indigo-500 dark:text-indigo-400 font-bold cursor-pointer hover:underline">$1</span>');
        formatted = formatted.replace(/(@[\w가-힣]+)/g, '<span class="text-violet-500 dark:text-violet-400 font-bold">$1</span>');
        return formatted;
    }

    async handleLike(btn) {
        if (!window.user) {
            if (typeof Swal !== 'undefined') {
                Swal.fire({
                    title: '로그인이 필요합니다',
                    text: "이 기능을 이용하시려면 로그인이 필요합니다.",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#4f46e5',
                    cancelButtonColor: '#d33',
                    confirmButtonText: '로그인 하러가기',
                    cancelButtonText: '취소'
                }).then((result) => {
                    if (result.isConfirmed) {
                        window.router.navigate('/login');
                    }
                });
            } else {
                if (confirm("로그인이 필요합니다.")) window.router.navigate('/login');
            }
            return;
        }

        const currentlyLiked = btn.classList.contains('text-yellow-400');
        const countSpan = btn.querySelector('.like-count');
        const icon = btn.querySelector('.material-symbols-outlined');

        // Optimistic UI Update
        if (currentlyLiked) {
            countSpan.innerText = Math.max(0, parseInt(countSpan.innerText) - 1);
            btn.classList.remove('text-yellow-400');
            btn.classList.add('text-gray-400');
            icon.style.fontVariationSettings = "'FILL' 0";
        } else {
            this.createParticles(btn);
            countSpan.innerText = parseInt(countSpan.innerText) + 1;
            btn.classList.remove('text-gray-400');
            btn.classList.add('text-yellow-400');
            icon.style.fontVariationSettings = "'FILL' 1";
        }

        try {
            await PlazaService.toggleLike(this.id, window.user.uid, currentlyLiked);
        } catch (e) {
            console.error("Like Error", e);
            // Revert on error? For now, silent fail is better UX than jumpy UI
        }
    }

    createParticles(target) {
        const limit = 8;
        for (let i = 0; i < limit; i++) {
            const p = document.createElement('div');
            p.innerText = '⭐';
            p.className = "absolute text-xs pointer-events-none animate-ping select-none";
            p.style.fontSize = Math.random() > 0.5 ? '12px' : '16px';

            const rect = target.getBoundingClientRect();
            const offsetX = (Math.random() - 0.5) * 40;
            const offsetY = (Math.random() - 0.5) * 40 - 20;

            const absoluteTop = rect.top + window.scrollY;
            const absoluteLeft = rect.left + window.scrollX;

            p.style.left = (absoluteLeft + 20 + offsetX) + 'px';
            p.style.top = (absoluteTop + 10 + offsetY) + 'px';
            p.style.zIndex = 9999;

            document.body.appendChild(p);
            setTimeout(() => p.remove(), 800);
        }
    }

    /**
     * Handle Repost Click
     * Shows logic: [Repost Immediately] or [Quote with Post]
     */
    handleRepostClick() {
        if (!window.user) {
            if (typeof Swal !== 'undefined') {
                Swal.fire({
                    title: '로그인이 필요합니다',
                    text: "이 기능을 이용하시려면 로그인이 필요합니다.",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#4f46e5',
                    cancelButtonColor: '#d33',
                    confirmButtonText: '로그인 하러가기',
                    cancelButtonText: '취소'
                }).then((result) => {
                    if (result.isConfirmed) {
                        window.router.navigate('/login');
                    }
                });
            } else {
                if (confirm("로그인이 필요합니다.")) window.router.navigate('/login');
            }
            return;
        }

        // Show SweetAlert2 Selection
        Swal.fire({
            title: '퍼가기',
            html: `
                <p class="text-xs text-gray-500 mb-4">이 이야기를 널리 알리시겠습니까?</p>
                <div class="flex flex-col gap-2">
                    <button id="btn-repost-now" class="w-full py-3 bg-green-50 text-green-700 hover:bg-green-100 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors">
                        <span class="material-symbols-outlined">repeat</span>
                        리포스트 (즉시 공유)
                    </button>
                    <button id="btn-quote-post" class="w-full py-3 bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors">
                        <span class="material-symbols-outlined">format_quote</span>
                        인용하기 (내 생각 쓰기)
                    </button>
                </div>
            `,
            showConfirmButton: false,
            showCloseButton: true,
            didOpen: (popup) => {
                popup.querySelector('#btn-repost-now').onclick = () => {
                    Swal.close();
                    this.executeRepost();
                };
                popup.querySelector('#btn-quote-post').onclick = () => {
                    Swal.close();
                    // [REFACTOR] Use Global Post Composer Modal
                    PostComposerModal.show({
                        quoteFeed: this.originalFeed || this.data
                    });
                };
            }
        });
    }

    async executeRepost() {
        try {
            await PlazaService.repostFeed(window.user, this.data);

            Swal.fire({
                icon: 'success',
                title: '공유 완료!',
                text: '내 썰화에 공유되었습니다.',
                timer: 1500,
                showConfirmButton: false
            });

            // Optimistic update of count?
            const countEl = this.element.querySelector('.count-repost');
            if (countEl) countEl.innerText = parseInt(countEl.innerText || 0) + 1;

        } catch (e) {
            console.error(e);
            Swal.fire('오류', '공유에 실패했습니다.', 'error');
        }
    }

}
